import React from 'react';
import styled from 'styled-components';

// Import all your local Fine Gems images
import tanzanite from '../../assets/image/Tanzanite.jpeg';
import blueZircon from '../../assets/image/Blue Zircon.jpeg';
import alexandrite from '../../assets/image/Alexandrite.jpeg';
import aquamarine from '../../assets/image/Aquamarine.jpeg';
import emerald from '../../assets/image/emerald.jpeg';
import rainbowMoonstone from '../../assets/image/Rainbow Moonstone.jpeg';
import spinel from '../../assets/image/Spinel.jpeg';
import peridot from '../../assets/image/Peridot.jpeg';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const HeroSection = styled.section`
  height: 60vh;
  min-height: 500px;
  background: linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)),
              url('https://images.unsplash.com/photo-1599643477877-530eb83abc8e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  margin-bottom: 60px;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.3);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled.h1`
  font-size: 64px;
  margin-bottom: 20px;
  letter-spacing: 3px;
  font-weight: 300;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  span {
    color: #c4a484;
    font-weight: 400;
  }
  
  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.8;
  opacity: 0.95;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  text-align: center;
  margin: 60px 0 40px;
  color: #1a2a3a;
  font-weight: 300;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 2px;
    background: #c4a484;
    margin: 20px auto;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const GemCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }
`;

const GemImageWrapper = styled.div`
  width: 100%;
  height: 280px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px;
    background: linear-gradient(to top, rgba(0,0,0,0.3), transparent);
    pointer-events: none;
  }
`;

const GemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  background-color: #f8f9fa;
  transition: transform 0.5s ease;
  
  ${GemCard}:hover & {
    transform: scale(1.05);
  }
`;

const GemInfo = styled.div`
  padding: 25px;
`;

const GemName = styled.h3`
  font-size: 24px;
  color: #1a2a3a;
  margin-bottom: 10px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`;

const GemDetails = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 15px;
  font-size: 14px;
`;

const GemProperties = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
`;

const Property = styled.span`
  background: #f8f9fa;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 12px;
  color: #555;
  border: 1px solid #e0e0e0;
  display: inline-flex;
  align-items: center;
  
  &:before {
    content: '✦';
    color: #c4a484;
    margin-right: 5px;
    font-size: 10px;
  }
`;

const Tag = styled.span`
  background: ${props => {
    if (props.tag === 'HOT') return '#ff4757';
    if (props.tag === 'NEW') return '#2ecc71';
    if (props.tag === 'SALE') return '#f39c12';
    if (props.tag === 'RARE') return '#9b59b6';
    if (props.tag === 'CLASSIC') return '#3498db';
    return '#c4a484';
  }};
  color: white;
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  white-space: nowrap;
`;

const OriginBadge = styled.span`
  background: #1a2a3a;
  color: white;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
  display: inline-block;
  margin-top: 10px;
`;

const FineGems = () => {
  // Fine Gems for the grid (excluding the hero image)
  const gems = [
    {
      name: "Tanzanite",
      image: tanzanite,
      description: "Discovered in 1967, tanzanite is prized for its trichroic properties - showing blue, purple, and burgundy. Found only in Tanzania, this gem is 1000 times rarer than diamonds.",
      properties: ["Hardness: 6.5", "Color: Blue-Violet", "Trichroic"],
      origin: "Tanzania",
      tag: "NEW",
      price: "$2,000 - $8,000 per carat"
    },
    {
      name: "Blue Zircon",
      image: blueZircon,
      description: "Natural zircon with brilliant blue color, known for its high dispersion and fire. Often confused with cubic zirconia, but natural zircon is a stunning gem in its own right.",
      properties: ["Hardness: 7.5", "Color: Sky Blue", "Dispersion: High"],
      origin: "Cambodia",
      tag: "HOT",
      price: "$1,500 - $5,000 per carat"
    },
    {
      name: "Alexandrite",
      image: alexandrite,
      description: "Rare color-change gemstone that appears green in daylight and red in incandescent light. Named after Tsar Alexander II, it's one of the most valuable gemstones.",
      properties: ["Hardness: 8.5", "Color: Color-Change", "Rarity: Exceptional"],
      origin: "Russia/Brazil",
      tag: "RARE",
      price: "$10,000 - $50,000+ per carat"
    },
    {
      name: "Aquamarine",
      image: aquamarine,
      description: "The serene blue gemstone of the sea, beloved for its clarity and calming energy. Named from Latin 'aqua marina' meaning 'seawater'.",
      properties: ["Hardness: 7.5-8", "Color: Blue-Green", "Birthstone: March"],
      origin: "Brazil",
      tag: "",
      price: "$500 - $3,000 per carat"
    },
    {
      name: "Emerald",
      image: emerald,
      description: "The classic green gemstone, prized since ancient times. Colombian emeralds are considered the finest, with their deep green color and exceptional transparency.",
      properties: ["Hardness: 7.5-8", "Color: Green", "Birthstone: May"],
      origin: "Colombia",
      tag: "CLASSIC",
      price: "$3,000 - $20,000+ per carat"
    },
    {
      name: "Rainbow Moonstone",
      image: rainbowMoonstone,
      description: "Feldspar gem with ethereal blue-white adularescence and rainbow flashes. Believed to bring good fortune and enhance intuition.",
      properties: ["Hardness: 6", "Color: White/Rainbow", "Effect: Adularescence"],
      origin: "India/Sri Lanka",
      tag: "",
      price: "$100 - $500 per carat"
    },
    {
      name: "Spinel",
      image: spinel,
      description: "Often mistaken for ruby, spinel comes in vibrant reds, pinks, and blues. Many famous 'rubies' in crown jewels were actually spinels.",
      properties: ["Hardness: 8", "Color: Various", "Clarity: Excellent"],
      origin: "Myanmar/Tajikistan",
      tag: "",
      price: "$500 - $5,000 per carat"
    },
    {
      name: "Peridot",
      image: peridot,
      description: "Vibrant green gemstone formed in volcanic rocks, the birthstone of August. One of the few gems that occurs in only one color: green.",
      properties: ["Hardness: 6.5-7", "Color: Lime Green", "Birthstone: August"],
      origin: "Arizona/China",
      tag: "",
      price: "$100 - $500 per carat"
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <span>Fine</span> Gems
          </HeroTitle>
          <HeroSubtitle>
            Exceptional gemstones for the discerning collector • Rare finds • Unique beauties
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>
      
      <SectionTitle>Our Fine Gem Collection</SectionTitle>
      <Grid>
        {gems.map((gem, index) => (
          <GemCard key={index}>
            <GemImageWrapper>
              <GemImage src={gem.image} alt={gem.name} />
            </GemImageWrapper>
            <GemInfo>
              <GemName>
                {gem.name}
                {gem.tag && <Tag tag={gem.tag}>{gem.tag}</Tag>}
              </GemName>
              <GemDetails>{gem.description}</GemDetails>
              <OriginBadge>📍 {gem.origin}</OriginBadge>
              <GemProperties>
                {gem.properties.map((prop, i) => (
                  <Property key={i}>{prop}</Property>
                ))}
              </GemProperties>
              <GemDetails style={{ marginTop: '10px', color: '#c4a484', fontWeight: '500' }}>
                {gem.price}
              </GemDetails>
            </GemInfo>
          </GemCard>
        ))}
      </Grid>
    </PageContainer>
  );
};

export default FineGems;